import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ArrowLeftIcon, MailIcon, LinkedInIcon, InstagramIcon, GithubIcon } from './Icons';
import { SectionTitle, P, A } from './common';
import { projectsData } from '../data/projectsData';
import { skillsData } from '../data/skillsData';
import headshot from '../assets/headshot.png';

const StaticPage = ({ setPage, initialSection, showBackButton = true }) => {
  const [activeSection, setActiveSection] = useState(initialSection || 'about');
  const [menuOpen, setMenuOpen] = useState(false);
  const scrollContainerRef = useRef(null);
  const aboutRef = useRef(null);
  const experienceRef = useRef(null);
  const skillsRef = useRef(null);
  const projectsRef = useRef(null);
  const patentsRef = useRef(null);
  const contactRef = useRef(null);

  const sections = useMemo(() => [
    { id: 'about', title: 'About', ref: aboutRef },
    { id: 'experience', title: 'Experience', ref: experienceRef },
    { id: 'skills', title: 'Skills', ref: skillsRef },
    { id: 'projects', title: 'Projects', ref: projectsRef },
    { id: 'patents', title: 'Patents', ref: patentsRef },
    { id: 'contact', title: 'Contact', ref: contactRef },
  ], []);

  // Scroll-spy via IntersectionObserver: no per-scroll-event DOM reads (issue #15)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) setActiveSection(entry.target.id);
      });
    }, { root: container, rootMargin: '-20% 0px -70% 0px', threshold: 0 });
    sections.forEach(s => { if (s.ref.current) observer.observe(s.ref.current); });
    return () => observer.disconnect();
  }, [sections]);

  // Fade-in-on-scroll reveal (issue #12); reveals once, then stops observing
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const els = container.querySelectorAll('.reveal');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { root: container, rootMargin: '0px 0px -10% 0px' });
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // deep link: jump to the requested section once refs are laid out
  useEffect(() => {
    if (!initialSection) return;
    const target = sections.find(s => s.id === initialSection);
    if (target && target.ref.current && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: target.ref.current.offsetTop - 80 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const form = e.target;
    const name = form.name.value.trim();
    const email = form.email.value.trim();
    const message = form.message.value.trim();
    const subject = encodeURIComponent(`Portfolio contact from ${name}`);
    const body = encodeURIComponent(`${message}\n\n— ${name} (${email})`);
    window.location.href = `mailto:greeshv03@gmail.com?subject=${subject}&body=${body}`;
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
    }, 600);
  };

  const scrollToSection = (section) => {
    if (section.ref.current && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: section.ref.current.offsetTop - 80,
        behavior: 'smooth'
      });
      window.history.replaceState(null, '', `#${section.id}`);
    }
  };

  return (
    <div
      ref={scrollContainerRef}
      className="static-page-scroll starfield-bg absolute top-0 left-0 w-full h-screen bg-[#0a192f] text-white overflow-y-auto"
    >
      {showBackButton && (
        <button
          onClick={() => setPage('main')}
          className="hidden md:flex fixed top-4 left-4 z-50 items-center gap-2 text-xs md:text-sm px-3 py-2 border border-teal-300 text-teal-300 rounded-md hover:bg-teal-400/10 hover:shadow-lg transition-all"
        >
          <ArrowLeftIcon />
          Back to Satellite
        </button>
      )}

      {/* Mobile sticky header with dropdown navigation (issues #3, #4) */}
      <header className="md:hidden fixed top-0 left-0 w-full z-50 bg-[#0a192f]/90 backdrop-blur-md border-b border-slate-800/50">
        <div className="flex items-center justify-between px-4 py-3">
          {showBackButton ? (
            <button
              onClick={() => setPage('main')}
              className="flex items-center gap-2 text-xs px-3 py-2 border border-teal-300 text-teal-300 rounded-md hover:bg-teal-400/10 transition-all"
            >
              <ArrowLeftIcon />
              Satellite
            </button>
          ) : (
            <span className="text-teal-300 font-bold tracking-wider text-sm">GREESH VARTHAN</span>
          )}
          <button
            onClick={() => setMenuOpen(open => !open)}
            aria-label="Toggle navigation menu"
            aria-expanded={menuOpen}
            className="p-2 border border-teal-300 text-teal-300 rounded-md hover:bg-teal-400/10 transition-all"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              {menuOpen
                ? <><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></>
                : <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>}
            </svg>
          </button>
        </div>
        {menuOpen && (
          <nav className="border-t border-slate-800/50">
            <ul className="flex flex-col">
              {sections.map(section => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(section);
                      setMenuOpen(false);
                    }}
                    className={`block px-6 py-3 font-mono text-sm ${activeSection === section.id ? 'text-teal-300' : 'text-slate-300'} hover:text-teal-300 transition-colors`}
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </header>

      <div className="relative md:flex">
        <aside className="hidden md:block md:w-1/3 lg:w-1/4 md:sticky md:top-0 md:h-screen md:py-24 md:px-6 lg:px-12 md:flex md:flex-col justify-between">
          <div className="pt-16">
            <h1 className="text-3xl lg:text-4xl font-bold text-teal-300 tracking-wider">
              Greesh Varthan A. S.
            </h1>
            <h2 className="text-xl lg:text-2xl font-light text-white mt-2">
              Aerospace Engineer
            </h2>
            <div className="telemetry-strip mt-5">
              <div><span className="t-key">LOC&nbsp;&nbsp;</span><span className="t-val">SEATTLE, WA</span></div>
              <div><span className="t-key">PRG&nbsp;&nbsp;</span><span className="t-val">UW MS AERO/ASTRO · DEC 2026</span></div>
              <div><span className="t-key">STS&nbsp;&nbsp;</span><span className="status-dot"></span><span className="t-val">OPEN TO ENGINEERING ROLES</span></div>
            </div>
            
            <nav className="mt-12">
              <ul className="flex flex-col gap-y-3">
                {sections.map(section => (
                  <li key={section.id}>
                    <a 
                      href={`#${section.id}`} 
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection(section);
                      }}
                      className={`font-mono text-slate-400 hover:text-teal-300 transition-all duration-200 ${activeSection === section.id ? 'nav-link-active' : ''}`}
                    >
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          <div className="flex gap-6 mt-8">
            <A href="mailto:greeshv03@gmail.com" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-teal-300 transition-colors transform hover:-translate-y-1"><MailIcon /></A>
            <A href="https://www.linkedin.com/in/greeshvarthan/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-teal-300 transition-colors transform hover:-translate-y-1"><LinkedInIcon /></A>
            <A href="https://github.com/greeshv03-ops" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-teal-300 transition-colors transform hover:-translate-y-1"><GithubIcon /></A>
            <A href="https://www.instagram.com/greeshvarthan/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-teal-300 transition-colors transform hover:-translate-y-1"><InstagramIcon /></A>
          </div>
        </aside>

        <main className="w-full md:w-2/3 lg:w-3/4 md:ml-auto pt-24 pb-24 px-4 md:px-6 lg:px-10">
          {/* Mobile Header */}
          <div className="md:hidden mb-8 pt-12">
             <h1 className="text-3xl font-bold text-teal-300 tracking-wider">
              Greesh Varthan A. S.
            </h1>
            <h2 className="text-xl font-light text-white mt-2">
              Aerospace Engineer
            </h2>
          </div>

          <section ref={aboutRef} id="about" className="mb-24 scroll-mt-24 reveal">
            <SectionTitle module="MAIN_BUS">ABOUT</SectionTitle>
            <div className="flex flex-col lg:flex-row lg:justify-evenly items-center gap-16">
              <div className="flex-shrink-0">
                <img 
                      src={ headshot }
                      alt="Headshot of Greesh Varthan"
                      className="rounded-full w-48 h-48 md:w-64 md:h-64 object-cover shadow-xl shadow-teal-400/40 border-4 border-slate-700"
                      onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/400x400/0a192f/FFFFFF?text=Image+Not+Found'; }}
                  />
              </div>
              <div className="w-full max-w-2xl">
                  <P>My name is Greesh Varthan and I’m currently pursuing my Masters in Aeronautics and Astronautics at UW! From what started as watching aircrafts soar in the sky from my terrace, became a long-lasting love which fueled my desire to design them! The roar from the engines, resonate with the ambition to become an Aerospace Engineer and one day enter the industry to design aircrafts that more robust and efficient than ever before.</P>
              </div>
            </div>
          </section>
          
          <section ref={experienceRef} id="experience" className="mb-24 scroll-mt-24 reveal">
            <SectionTitle module="SOLAR_ARRAY_R">EXPERIENCE</SectionTitle>
              <div className="experience-timeline">
                <div className="timeline-item">
                  <div className="timeline-icon"></div>
                  <div className="timeline-content">
                    <h3 className="text-white font-semibold">Mechanical Design Engineer | Elementrailer</h3>
                    <p className="text-xs text-slate-300 mb-2">Oct 2025 - Present</p>
                    <ul className="list-none pl-4 text-slate-300">
                        <li className="relative before:content-['▹'] before:absolute before:left-[-1em] before:text-teal-300">Design the frame, suspension, and battery enclosure for a 10,000 lb GVWR electric trailer in SolidWorks/Onshape, from concept through drawings and BOMs released to manufacturing and suppliers.</li>
                        <li className="relative before:content-['▹'] before:absolute before:left-[-1em] before:text-teal-300">Root-caused a recurring assembly failure where every part passed inspection: unmanaged tolerance chains on legacy drawings. Fixed the drawings and the release process behind them, cutting fit and rework issues about 25%.</li>
                        <li className="relative before:content-['▹'] before:absolute before:left-[-1em] before:text-teal-300">Conduct ANSYS structural, fatigue, and thermal analyses on the chassis and mounting systems; drove reinforcement that raised minimum safety factor from 1.8 to 2.3.</li>
                        <li className="relative before:content-['▹'] before:absolute before:left-[-1em] before:text-teal-300">Build the team's engineering software: a Python electro-thermal battery model with a GUI, used for sizing and cooling trade studies and correlated against prototype test data.</li>
                        <li className="relative before:content-['▹'] before:absolute before:left-[-1em] before:text-teal-300">Coordinate directly with vendors in China on chassis, battery, and cable components to keep prototype builds on schedule.</li>
                    </ul>
                  </div>
                </div>

                <div className="timeline-item">
                  <div className="timeline-icon"></div>
                  <div className="timeline-content">
                    <h3 className="text-white font-semibold">Graduate Researcher, MS Thesis | University of Washington</h3>
                    <p className="text-xs text-slate-300 mb-2">Sep 2024 - Present</p>
                    <ul className="list-none pl-4 text-slate-300">
                        <li className="relative before:content-['▹'] before:absolute before:left-[-1em] before:text-teal-300">Run an ASTM D6415 curved-beam test campaign on chopped-prepreg discontinuous fiber composites, spanning bend radius, laminate thickness, and platelet geometry, to evaluate the standard as a design-allowables method.</li>
                        <li className="relative before:content-['▹'] before:absolute before:left-[-1em] before:text-teal-300">Instrument tests with camera-based DIC strain measurement: authored the camera-calibration work instructions and wrote an independent Python DIC solver as a cross-check on GOM Correlate.</li>
                        <li className="relative before:content-['▹'] before:absolute before:left-[-1em] before:text-teal-300">Reduce failure data through the Lekhnitskii closed-form solution to interlaminar strength and B-basis design allowable estimates, identifying failure modes by microscopy and verifying fiber orientation with micro-CT.</li>
                    </ul>
                  </div>
                </div>

                <div className="timeline-item">
                  <div className="timeline-icon"></div>
                  <div className="timeline-content">
                    <h3 className="text-white font-semibold">Structures Member | Washington Hyperloop</h3>
                    <p className="text-xs text-slate-300 mb-2">Sep 2024 - Dec 2024</p>
                    <ul className="list-none pl-4 text-slate-300">
                        <li className="relative before:content-['▹'] before:absolute before:left-[-1em] before:text-teal-300">Collaborated with Washington Hyperloop's structures team to design the structural framework for a vertical Tunnel Boring Machine (TBM).</li>
                        <li className="relative before:content-['▹'] before:absolute before:left-[-1em] before:text-teal-300">Applied principles of materials science and mechanics to select appropriate materials for the TBM structure, balancing strength and weight requirements.</li>
                        <li className="relative before:content-['▹'] before:absolute before:left-[-1em] before:text-teal-300">Utilized SolidWorks to create detailed 3D models of the TBM's structural components and conducted FEA simulations to determine stress and failure.</li>
                    </ul>
                  </div>
                </div>
      
                <div className="timeline-item">
                  <div className="timeline-icon"></div>
                  <div className="timeline-content">
                    <h3 className="text-white font-semibold">CFD Research Intern | Indian Space Research Organization (ISRO)</h3>
                    <p className="text-xs text-slate-300 mb-2">Jan 2024 - Apr 2024</p>
                    <ul className="list-none pl-4 text-slate-300">
                        <li className="relative before:content-['▹'] before:absolute before:left-[-1em] before:text-teal-300">Design and analysis of advanced propeller systems for autonomous space exploration bots, tailored for operation in low-gravity and vacuum environments.</li>
                        <li className="relative before:content-['▹'] before:absolute before:left-[-1em] before:text-teal-300">Presented design findings and recommendations of the propeller systems to senior engineers and contributed to iterative design improvements.</li>
                        <li className="relative before:content-['▹'] before:absolute before:left-[-1em] before:text-teal-300">Employed STAR-CCM+ to perform detailed CFD simulations, optimizing the aerodynamic performance and efficiency of propeller systems under provided conditions.</li>
                        <li className="relative before:content-['▹'] before:absolute before:left-[-1em] before:text-teal-300">Worked on-site at a manufacturing vendor facility to fast-track design and development of hardware for the Gaganyaan mission, India's first human spaceflight program.</li>
                    </ul>
                  </div>
                </div>

                <div className="timeline-item">
                  <div className="timeline-icon"></div>
                  <div className="timeline-content">
                    <h3 className="text-white font-semibold">Software Development Intern | Single Point Solutions Pvt. Ltd.</h3>
                    <p className="text-xs text-slate-300 mb-2">Jun 2023 - Jul 2023 · Hyderabad, India</p>
                    <ul className="list-none pl-4 text-slate-300">
                        <li className="relative before:content-['▹'] before:absolute before:left-[-1em] before:text-teal-300">Using Python, automated routine data processing tasks and performed small-scale data analysis with NumPy and Pandas.</li>
                        <li className="relative before:content-['▹'] before:absolute before:left-[-1em] before:text-teal-300">Contributed to internal Python tools alongside the development team, working within their code review and delivery process.</li>
                    </ul>
                  </div>
                </div>
              </div>
          </section>
          
          <section ref={skillsRef} id="skills" className="mb-24 scroll-mt-24 reveal">
            <span className="module-eyebrow" style={{ textAlign: 'center' }}>MODULE // COMMS_DISH</span>
            <div className="skill-tree">
              {/* Main Title Box */}
              <div className="bg-slate-900 border border-teal-300 text-teal-300 font-bold px-6 py-3 rounded-md shadow-lg">
                MY SKILLS
              </div>
              <div className="skill-trunk"></div>
              
              {/* Branches Container */}
              <div className="skill-branches">
                {/* Branch 1: Languages */}
                <div className="skill-branch">
                  <div className="skill-category-title">Languages</div>
                  <div className="skill-tags">
                    {skillsData.languages.map(skill => (
                      <span key={skill} className="skill-tag">{skill}</span>
                    ))}
                  </div>
                </div>
                
                {/* Branch 2: Software */}
                <div className="skill-branch">
                  <div className="skill-category-title">Software</div>
                  <div className="skill-tags">
                    {skillsData.software.map(skill => (
                      <span key={skill} className="skill-tag">{skill}</span>
                    ))}
                  </div>
                </div>
                
                {/* Branch 3: Concepts */}
                <div className="skill-branch">
                  <div className="skill-category-title">Concepts</div>
                  <div className="skill-tags">
                    {skillsData.concepts.map(skill => (
                      <span key={skill} className="skill-tag">{skill}</span>
                    ))}
                  </div>
                </div>

                {/* Branch 4: Spoken Languages */}
                <div className="skill-branch">
                  <div className="skill-category-title">Spoken Languages</div>
                  <div className="skill-tags">
                    {skillsData.spoken.map(skill => (
                      <span key={skill} className="skill-tag">{skill}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
          
          <section ref={projectsRef} id="projects" className="mb-24 scroll-mt-24 reveal">
            <SectionTitle module="SOLAR_ARRAY_L">PROJECTS</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {projectsData.map(p => (
                <div key={p.id} className="hud-card bg-slate-900/50 border border-slate-700 rounded-lg p-6 flex flex-col hover:shadow-lg hover:border-teal-300/50">
                  <img
                    src={p.image}
                    alt={`Visualization for ${p.title}`}
                    className="rounded-md w-full h-48 md:h-64 object-fit mb-4 border border-slate-600"
                    onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/600x400/0a192f/FFFFFF?text=Image+Not+Found'; }}
                  />
                  <h3 className="text-xl font-bold text-teal-300 mb-2">{p.title}</h3>
                  <p className="text-white font-light flex-grow">{p.long}</p>
                  {p.url && (
                    <a href={p.url} target="_blank" rel="noopener noreferrer" className="visit-link">VISIT ↗</a>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section ref={patentsRef} id="patents" className="mb-24 scroll-mt-24 reveal">
            <SectionTitle module="PAYLOAD">PATENTS</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="hud-card bg-slate-900/50 border border-slate-700 rounded-lg p-6 hover:shadow-lg hover:border-teal-300/50">
                <h3 className="text-xl font-bold text-teal-300 mb-2">Dynamic Oxygen Concentration Machine Based on Fuzzy Logic (OCM)</h3>
                <p className="text-xs text-slate-300 mb-3 font-mono">ID 357353-001 · Awarded Jan 27, 2022 · IP India Design Registry</p>
                <p className="text-white font-light">A fuzzy-logic-controlled machine that dynamically adjusts oxygen concentration to demand rather than delivering a fixed output.</p>
              </div>
              <div className="hud-card bg-slate-900/50 border border-slate-700 rounded-lg p-6 hover:shadow-lg hover:border-teal-300/50">
                <h3 className="text-xl font-bold text-teal-300 mb-2">Dynamic Oxygen Supply Machine Based on Fuzzy Logic (OSM)</h3>
                <p className="text-xs text-slate-300 mb-3 font-mono">ID 357354-001 · Awarded Jan 27, 2022 · IP India Design Registry</p>
                <p className="text-white font-light">A companion fuzzy-logic system that modulates oxygen supply delivery dynamically in response to changing conditions.</p>
              </div>
            </div>
          </section>

          <section ref={contactRef} id="contact" className="mb-24 scroll-mt-24 reveal">
            <SectionTitle module="ANTENNA">CONTACT</SectionTitle>
            <P>I'm always open to discussing new projects, creative ideas, or opportunities. Feel free to reach out using the form below or connect with me on social media.</P>
            
            {submitted ? (
              <div className="form-success-message">
                <p className="font-semibold">Thank you!</p>
                <p>Your email app should have opened with the message prefilled — just hit send. If it didn't, email me directly at greeshv03@gmail.com.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-1">Name</label>
                  <input type="text" id="name" name="name" className="form-input" required />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-1">Email</label>
                  <input type="email" id="email" name="email" className="form-input" required />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-1">Message</label>
                  <textarea id="message" name="message" rows="5" className="form-textarea" required></textarea>
                </div>
                <div className='flex justify-center'>
                  <button type="submit" className="form-button" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </div>
              </form>
            )}
          </section>
          
          {/* Mobile Footer with Socials */}
          <div className="md:hidden flex justify-center gap-6 mt-16">
            <A href="mailto:greeshv03@gmail.com" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-teal-300 transition-colors transform hover:-translate-y-1"><MailIcon /></A>
            <A href="https://www.linkedin.com/in/greeshvarthan/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-teal-300 transition-colors transform hover:-translate-y-1"><LinkedInIcon /></A>
            <A href="https://github.com/greeshv03-ops" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-teal-300 transition-colors transform hover:-translate-y-1"><GithubIcon /></A>
            <A href="https://www.instagram.com/greeshvarthan/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-teal-300 transition-colors transform hover:-translate-y-1"><InstagramIcon /></A>
          </div>
        </main>
      </div>
    </div>
  );
};

export default StaticPage;